import { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { Skeleton } from "@/components/ui/skeleton";
import { buildAuthUrl } from "@/lib/auth-redirect";

interface ProtectedRouteProps {
  children: ReactNode;
  /** Where to send unauthenticated users. Defaults to "/auth". Use "/" for public marketing fallback. */
  redirectTo?: string;
  /** If true, preserves the attempted path as ?redirect=… so user can be sent back after login. */
  preserveRedirect?: boolean;
}

const ProtectedRoute = ({ children, redirectTo = "/auth", preserveRedirect = true }: ProtectedRouteProps) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div data-testid="protected-skeleton" className="min-h-screen bg-background flex flex-col">
        {/* Mobile header placeholder */}
        <div className="md:hidden flex items-center justify-between gap-3 px-4 h-14 border-b border-border">
          <Skeleton className="h-8 w-8 rounded-full" />
          <Skeleton className="h-5 w-32" />
          <Skeleton className="h-8 w-8 rounded-md" />
        </div>
        {/* Desktop header placeholder */}
        <div className="hidden md:flex items-center justify-between gap-4 px-6 h-16 border-b border-border">
          <Skeleton className="h-6 w-40" />
          <div className="flex items-center gap-3">
            <Skeleton className="h-8 w-20" />
            <Skeleton className="h-8 w-20" />
            <Skeleton className="h-8 w-8 rounded-full" />
          </div>
        </div>
        {/* Filters / toolbar placeholder */}
        <div className="px-4 md:px-6 py-3 border-b border-border flex flex-wrap gap-2">
          <Skeleton className="h-9 w-24 rounded-full" />
          <Skeleton className="h-9 w-28 rounded-full" />
          <Skeleton className="h-9 w-20 rounded-full" />
          <Skeleton className="h-9 w-32 rounded-full hidden sm:block" />
        </div>
        {/* Body content placeholder */}
        <div className="flex-1 px-4 md:px-6 py-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Skeleton className="h-40 w-full rounded-xl" />
          <Skeleton className="h-40 w-full rounded-xl" />
          <Skeleton className="h-40 w-full rounded-xl" />
          <Skeleton className="h-40 w-full rounded-xl hidden sm:block" />
          <Skeleton className="h-40 w-full rounded-xl hidden lg:block" />
          <Skeleton className="h-40 w-full rounded-xl hidden lg:block" />
        </div>
      </div>
    );
  }

  if (!user) {
    const target =
      preserveRedirect && redirectTo === "/auth"
        ? buildAuthUrl(location.pathname + location.search)
        : redirectTo;
    return <Navigate to={target} replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;